import {
  AUTOWALK_RATE,
  DIVIDEND_BONUS,
  HORIZON_CEILINGS,
  RESONATOR_BONUS,
  REWARDS,
  STRIDE_STEPS,
  UPGRADE_BY_ID,
  type TrialLength,
  type UpgradeId,
} from './balance';
import { famousAt, FAMOUS_NUMBERS, type FamousNumber } from './famous';
import { completesTwin, isPrime, primeIndex, primesUpTo, twinPairsUpTo } from './numbers';
import type { GameState } from './state';

/**
 * Game rules. Pure functions over GameState with no DOM, timers or randomness, so every
 * rule is unit-testable and the UI only reacts to the events these functions return.
 */

export type GameEvent =
  | { type: 'step'; n: number; fresh: boolean }
  | { type: 'prime'; n: number; index: number; bonus: number }
  | { type: 'twin'; lo: number; hi: number; bonus: number }
  | { type: 'famous'; entries: readonly FamousNumber[]; bonus: number }
  | { type: 'blocked'; edge: 'ceiling' | 'floor'; limit: number };

// ---------------------------------------------------------------------------------------
// Derived values
// ---------------------------------------------------------------------------------------

const at = <T>(table: readonly T[], level: number): T => table[Math.min(level, table.length - 1)]!;

export const ceiling = (s: GameState) => at(HORIZON_CEILINGS, s.upgrades.horizon);
export const floor = (s: GameState) => (s.upgrades.inverter > 0 ? -ceiling(s) : 0);
export const stride = (s: GameState) => at(STRIDE_STEPS, s.upgrades.stride);
export const autowalkRate = (s: GameState) => at(AUTOWALK_RATE, s.upgrades.autowalk);
export const primeBonus = (s: GameState) => at(DIVIDEND_BONUS, s.upgrades.dividend);
export const twinBonus = (s: GameState) => at(RESONATOR_BONUS, s.upgrades.resonator);

/** Prime medals earned: every prime inside the visited range. */
export const medals = (s: GameState) => primesUpTo(s.maxVisited);
export const twinPairs = (s: GameState) => twinPairsUpTo(s.maxVisited);
export const visitedCount = (s: GameState) => s.maxVisited - s.minVisited + 1;

export function isVisited(s: GameState, n: number): boolean {
  return n >= s.minVisited && n <= s.maxVisited;
}

export function famousFound(s: GameState): FamousNumber[] {
  return FAMOUS_NUMBERS.filter((f) => isVisited(s, f.n));
}

/** True when the next step in the current direction would cross the edge. */
export function atEdge(s: GameState): boolean {
  return s.direction === 1 ? s.position >= ceiling(s) : s.position <= floor(s);
}

// ---------------------------------------------------------------------------------------
// Walking
// ---------------------------------------------------------------------------------------

function earn(s: GameState, amount: number): void {
  s.points += amount;
  s.lifetimePoints += amount;
}

/** Walk one integer in the current direction. Returns false if blocked at an edge. */
export function stepOnce(s: GameState, out: GameEvent[]): boolean {
  if (atEdge(s)) {
    out.push(
      s.direction === 1
        ? { type: 'blocked', edge: 'ceiling', limit: ceiling(s) }
        : { type: 'blocked', edge: 'floor', limit: floor(s) },
    );
    return false;
  }

  const n = s.position + s.direction;
  s.position = n;
  s.steps++;

  const fresh = n > s.maxVisited || n < s.minVisited;
  out.push({ type: 'step', n, fresh });
  if (!fresh) return true;

  if (n > s.maxVisited) s.maxVisited = n;
  else s.minVisited = n;
  earn(s, REWARDS.newInteger);

  if (isPrime(n)) {
    const bonus = primeBonus(s);
    earn(s, bonus);
    out.push({ type: 'prime', n, index: primeIndex(n), bonus });
    if (completesTwin(n)) {
      const tb = twinBonus(s);
      earn(s, tb);
      out.push({ type: 'twin', lo: n - 2, hi: n, bonus: tb });
    }
  }

  const entries = famousAt(n);
  if (entries.length > 0) {
    const bonus = REWARDS.famousNumber * entries.length;
    earn(s, bonus);
    out.push({ type: 'famous', entries, bonus });
  }
  return true;
}

/** Walk up to `count` integers, stopping early at an edge. */
export function walk(s: GameState, count: number, out: GameEvent[] = []): GameEvent[] {
  for (let i = 0; i < count; i++) {
    if (!stepOnce(s, out)) break;
  }
  return out;
}

/** A player tap: walks `stride` integers. */
export function tap(s: GameState, out: GameEvent[] = []): GameEvent[] {
  s.taps++;
  return walk(s, stride(s), out);
}

/**
 * Auto-walk for `dtMs` of elapsed time. `carry` holds fractional steps between frames;
 * returns the new carry. Silently idles at an edge instead of emitting "blocked".
 */
export function autowalk(s: GameState, dtMs: number, carry: number, out: GameEvent[]): number {
  const rate = autowalkRate(s);
  if (rate === 0 || atEdge(s)) return 0;
  const total = carry + (rate * dtMs) / 1000;
  // Epsilon absorbs float drift so 120 frames of 1/120 s really is one step.
  const whole = Math.floor(total + 1e-9);
  for (let i = 0; i < whole && !atEdge(s); i++) stepOnce(s, out);
  return Math.max(0, total - whole);
}

export function toggleDirection(s: GameState): boolean {
  if (s.upgrades.inverter === 0) return false;
  s.direction = s.direction === 1 ? -1 : 1;
  return true;
}

// ---------------------------------------------------------------------------------------
// Shop
// ---------------------------------------------------------------------------------------

export type PurchaseStatus =
  | { kind: 'maxed' }
  | { kind: 'locked'; needs: string }
  | { kind: 'available'; cost: number; affordable: boolean };

export function purchaseStatus(s: GameState, id: UpgradeId): PurchaseStatus {
  const def = UPGRADE_BY_ID[id];
  const level = s.upgrades[id];
  if (level >= def.costs.length) return { kind: 'maxed' };
  const missing = def.requires?.find((r) => s.upgrades[r.id] < r.level);
  if (missing) return { kind: 'locked', needs: UPGRADE_BY_ID[missing.id].name };
  const horizonNeeded = def.minHorizon?.[level] ?? 0;
  if (s.upgrades.horizon < horizonNeeded) {
    return { kind: 'locked', needs: `Horizon ${horizonNeeded}` };
  }
  const cost = def.costs[level]!;
  return { kind: 'available', cost, affordable: s.points >= cost };
}

/** Buys the next level of an upgrade. Returns the new level, or null if not possible. */
export function buy(s: GameState, id: UpgradeId): number | null {
  const status = purchaseStatus(s, id);
  if (status.kind !== 'available' || !status.affordable) return null;
  s.points -= status.cost;
  s.upgrades[id]++;
  return s.upgrades[id];
}

// ---------------------------------------------------------------------------------------
// Trials
// ---------------------------------------------------------------------------------------

/** Records a speed-trial result. Returns true if it is a new personal best. */
export function recordTrial(s: GameState, length: TrialLength, taps: number): boolean {
  if (taps <= s.trialBests[length]) return false;
  s.trialBests[length] = taps;
  return true;
}
